import { useMemo } from 'react';
import { format } from 'date-fns';
import { OptionProps } from 'react-select';
import closeSvg from 'assets/icons/close.svg';
import BaseSelect from 'lib/BaseSelect';
import { createMultiSelectPlaceholder, MultiSelectOption } from 'lib/BaseSelect/MultiSelectComponents';
import { BaseSelectOptionProps } from 'lib/BaseSelect/BaseSelect.types';
import BaseSelectOption from 'lib/BaseSelect/BaseSelectOption';
import Chip, { Color, Width } from 'lib/Chip';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import Button, { Color as ButtonColor, Size as ButtonSize } from 'lib/Button';
import { CodeNameModel } from 'components/common/types';
import { DATE_DISPLAY_FORMAT } from 'constants/dateTimePicker';
import DateRangeSelect from 'lib/DatePicker/DateRangeSelect';
import { AVAILABLE_COLOR } from 'components/pages/Planner/PlannerMap/utils/mapColors';
import { UseCampaignPlanFilters } from './useCampaignPlanFilters';
import { FilterColorMap } from './useFilterColors';

interface CampaignPlanMapFiltersProps {
  filters: UseCampaignPlanFilters;
}

const createColorDotOption =
  (colorMap: FilterColorMap) =>
  ({ isSelected, innerRef, innerProps, children, data }: BaseSelectOptionProps & OptionProps<CodeNameModel>) => (
    <BaseSelectOption isMultiple isSelected={isSelected} innerRef={innerRef} innerProps={innerProps}>
      <span className="flex items-center gap-2">
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: colorMap[data.code]?.hex ?? AVAILABLE_COLOR }}
        />
        {children}
      </span>
    </BaseSelectOption>
  );

const CampaignPlanMapFilters = ({ filters }: CampaignPlanMapFiltersProps): React.ReactElement => {
  const {
    isPOIsIncluded,
    selectedStartDate,
    selectedEndDate,
    selectedMediaType,
    selectedChannels,
    selectedFormats,
    selectedPois,
    minDate,
    maxDate,
    mediaTypeOptions,
    channelOptions,
    formatOptions,
    poiOptions,
    hasActiveFilters,
    channelColors,
    formatColors,
    setSelectedStartDate,
    setSelectedEndDate,
    handleMediaTypeSelect,
    handleChannelSelect,
    handleFormatSelect,
    handlePoiSelect,
    removeChannel,
    removeFormat,
    removePoi,
    clearAll,
  } = filters;

  const ChannelOption = useMemo(() => createColorDotOption(channelColors), [channelColors]);
  const FormatOption = useMemo(() => createColorDotOption(formatColors), [formatColors]);

  return (
    <div data-test-id="campaign-plan-map-filters">
      <div className="header-base mb-4">Map</div>
      <div className={`grid ${isPOIsIncluded && poiOptions.length > 0 ? 'grid-cols-5' : 'grid-cols-4'} gap-4 mb-4`}>
        <div>
          <div className="sub-header-base text-essential-secondary mb-1">Date</div>
          <DateRangeSelect
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            onStartDateChange={setSelectedStartDate}
            onEndDateChange={setSelectedEndDate}
            minDate={minDate}
            maxDate={maxDate}
            defaultMonth={minDate}
            isClearable
            dataTestId="filter-date-range"
            dataPendoId="campaign-plan-filter-date-range"
          />
        </div>

        <div>
          <div className="sub-header-base text-essential-secondary mb-1">Media type</div>
          <BaseSelect
            name="mediaType"
            dataTestId="filter-media-type"
            dataPendoId="campaign-plan-filter-media-type"
            placeholder="Select media type"
            options={mediaTypeOptions}
            selectedValue={selectedMediaType}
            onSelect={handleMediaTypeSelect}
            isClearable
            withPortal={false}
          />
        </div>

        <div>
          <div className="sub-header-base text-essential-secondary mb-1">Channel</div>
          <BaseSelect
            name="channel"
            dataTestId="filter-channel"
            dataPendoId="campaign-plan-filter-channel"
            placeholder="Select channel"
            options={channelOptions}
            selectedValue={selectedChannels}
            onSelect={handleChannelSelect}
            isMultiple
            isSelectionVisible={false}
            isClearable
            closeMenuOnSelect={false}
            withPortal={false}
            customComponents={{
              Placeholder: createMultiSelectPlaceholder('Channel', 'Select channel'),
              Option: ChannelOption,
            }}
          />
        </div>

        <div>
          <div className="sub-header-base text-essential-secondary mb-1">Frame size</div>
          <BaseSelect
            name="frame-size"
            dataTestId="filter-frame-size"
            dataPendoId="campaign-plan-filter-frame-size"
            placeholder="Select frame size"
            options={formatOptions}
            selectedValue={selectedFormats}
            onSelect={handleFormatSelect}
            isMultiple
            isSelectionVisible={false}
            isClearable
            closeMenuOnSelect={false}
            withPortal={false}
            customComponents={{
              Placeholder: createMultiSelectPlaceholder('Frame size', 'Select frame size'),
              Option: FormatOption,
            }}
          />
        </div>

        {isPOIsIncluded && poiOptions.length > 0 && (
          <div>
            <div className="sub-header-base text-essential-secondary mb-1">POI</div>
            <BaseSelect
              name="poi"
              dataTestId="filter-poi"
              dataPendoId="campaign-plan-filter-poi"
              placeholder="Select POI"
              options={poiOptions}
              selectedValue={selectedPois}
              onSelect={handlePoiSelect}
              isMultiple
              isSelectionVisible={false}
              isClearable
              closeMenuOnSelect={false}
              withPortal={false}
              customComponents={{
                Placeholder: createMultiSelectPlaceholder('POI', 'Select POI'),
                Option: MultiSelectOption,
              }}
            />
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 mb-4 min-h-[34px]" data-test-id="filter-chips">
        <div className="flex flex-wrap items-start gap-2 flex-1">
          {selectedStartDate && selectedEndDate && (
            <Chip
              label={`${format(selectedStartDate, DATE_DISPLAY_FORMAT)} - ${format(selectedEndDate, DATE_DISPLAY_FORMAT)}`}
              color={Color.PRIMARY}
              width={Width.FULL}
              truncateLabel={false}
              onRemove={() => {
                setSelectedStartDate(undefined);
                setSelectedEndDate(undefined);
              }}
              dataTestId="chip-date-range"
            />
          )}
          {selectedMediaType && (
            <Chip
              label={selectedMediaType.name}
              color={Color.PRIMARY}
              onRemove={() => handleMediaTypeSelect('mediaType', [])}
              dataTestId="chip-media-type"
            />
          )}
          {selectedChannels.map((channel) => (
            <Chip
              key={channel.code}
              label={channel.name}
              color={Color.DARK_GREY}
              wrapperClassName="[&_button]:!text-neutral-50"
              wrapperStyle={{ backgroundColor: channelColors[channel.code]?.hex }}
              onRemove={() => removeChannel(channel.code)}
              dataTestId={`chip-channel-${channel.code}`}
            />
          ))}
          {selectedFormats.map((selectedFormat) => (
            <Chip
              key={selectedFormat.code}
              label={selectedFormat.name}
              color={Color.DARK_GREY}
              wrapperClassName="[&_button]:!text-neutral-50"
              wrapperStyle={{ backgroundColor: formatColors[selectedFormat.code]?.hex }}
              onRemove={() => removeFormat(selectedFormat.code)}
              dataTestId={`chip-format-${selectedFormat.code}`}
            />
          ))}
          {selectedPois.map((poi) => (
            <Tooltip key={poi.code} direction={TooltipDirection.BOTTOM} tooltip={poi.name}>
              <div>
                <Chip
                  label={poi.name}
                  color={Color.PRIMARY}
                  onRemove={() => removePoi(poi.code)}
                  dataTestId={`chip-poi-${poi.code}`}
                />
              </div>
            </Tooltip>
          ))}
        </div>
        {hasActiveFilters && (
          <Button
            svg={closeSvg}
            label="Clear all"
            color={ButtonColor.SECONDARY}
            size={ButtonSize.MEDIUM}
            onClick={clearAll}
            dataTestId="clear-all-filters"
            classNames="shrink-0"
          />
        )}
      </div>
    </div>
  );
};

export default CampaignPlanMapFilters;
